export interface ScoringQuestion {
  id: string;
  label: string;
}

export interface ScoringCategory {
  id: string;
  title: string;
  emoji: string;
  objective: string;
  color: string; // tailwind / hex for charts
  questions: ScoringQuestion[];
}

export const SCORING_CATEGORIES: ScoringCategory[] = [
  {
    id: "sommeil",
    title: "Sommeil",
    emoji: "🌙",
    objective: "Évaluer la qualité de récupération physique et nerveuse.",
    color: "#6366f1",
    questions: [
      { id: "sommeil_1", label: "Je m'endors facilement le soir" },
      { id: "sommeil_2", label: "Mon sommeil est réparateur" },
      { id: "sommeil_3", label: "Je dors paisiblement toute la nuit" },
      { id: "sommeil_4", label: "Je me sens reposé(e) au réveil" },
      { id: "sommeil_5", label: "Je récupère bien grâce à mon sommeil" },
    ],
  },
  {
    id: "digestion",
    title: "Digestion",
    emoji: "🥗",
    objective: "Évaluer le confort digestif et l'assimilation.",
    color: "#10b981",
    questions: [
      { id: "digestion_1", label: "Je digère facilement mes repas" },
      { id: "digestion_2", label: "Mon ventre est confortable après les repas" },
      { id: "digestion_3", label: "Mon transit est régulier" },
      { id: "digestion_4", label: "Je me sens léger(e) après avoir mangé" },
      { id: "digestion_5", label: "Je tolère bien les aliments du quotidien" },
    ],
  },
  {
    id: "stress",
    title: "Stress & Charge mentale",
    emoji: "🧠",
    objective: "Évaluer l'équilibre émotionnel et nerveux.",
    color: "#a855f7",
    questions: [
      { id: "stress_1", label: "Je me sens serein(e) au quotidien" },
      { id: "stress_2", label: "J'arrive facilement à déconnecter mentalement" },
      { id: "stress_3", label: "Je gère bien ma charge mentale" },
      { id: "stress_4", label: "Je prends suffisamment de temps pour moi" },
      { id: "stress_5", label: "Je me sens émotionnellement équilibré(e)" },
    ],
  },
  {
    id: "energie",
    title: "Énergie",
    emoji: "⚡",
    objective: "Évaluer la vitalité globale et la capacité de récupération.",
    color: "#f59e0b",
    questions: [
      { id: "energie_1", label: "Je me sens en forme dans la journée" },
      { id: "energie_2", label: "Mon niveau d'énergie est stable" },
      { id: "energie_3", label: "Je récupère facilement après une journée intense" },
      { id: "energie_4", label: "Je me sens motivé(e) et dynamique" },
      { id: "energie_5", label: "Je conserve une bonne concentration au quotidien" },
    ],
  },
  {
    id: "alimentation",
    title: "Alimentation",
    emoji: "🍎",
    objective: "Évaluer l'équilibre alimentaire général.",
    color: "#ef4444",
    questions: [
      { id: "alimentation_1", label: "Mon alimentation est équilibrée" },
      { id: "alimentation_2", label: "Je mange des produits frais régulièrement" },
      { id: "alimentation_3", label: "Je bois suffisamment d'eau au quotidien" },
      { id: "alimentation_4", label: "Je me sens bien après mes repas" },
      { id: "alimentation_5", label: "Mon alimentation soutient mon énergie" },
    ],
  },
  {
    id: "mouvement",
    title: "Mouvement & Récupération",
    emoji: "🏃",
    objective: "Évaluer l'équilibre entre activité physique et récupération.",
    color: "#06b6d4",
    questions: [
      { id: "mouvement_1", label: "Je bouge suffisamment au quotidien" },
      { id: "mouvement_2", label: "Je pratique une activité physique régulière" },
      { id: "mouvement_3", label: "Mon corps récupère bien après l'effort" },
      { id: "mouvement_4", label: "Je prends du temps pour récupérer et ralentir" },
      { id: "mouvement_5", label: "Je me sens bien physiquement dans mon corps" },
    ],
  },
];

export const RESPONSE_OPTIONS = [
  { value: 0, label: "Jamais" },
  { value: 1, label: "Rarement" },
  { value: 2, label: "Parfois" },
  { value: 3, label: "Souvent" },
  { value: 4, label: "Toujours" },
];

export function interpretCategoryScore(score: number): { label: string; color: string } {
  if (score >= 16) return { label: "Équilibre satisfaisant", color: "#10b981" };
  if (score >= 11) return { label: "Axe à soutenir", color: "#f59e0b" };
  if (score >= 6) return { label: "Déséquilibre modéré", color: "#f97316" };
  return { label: "Axe prioritaire", color: "#ef4444" };
}

export function interpretGlobalScore(score: number): { label: string; color: string } {
  if (score >= 96) return { label: "Vitalité globale satisfaisante", color: "#10b981" };
  if (score >= 66) return { label: "Équilibre fragile", color: "#f59e0b" };
  if (score >= 36) return { label: "Terrain à soutenir", color: "#f97316" };
  return { label: "Vitalité affaiblie", color: "#ef4444" };
}

export function computeCategoryScore(
  category: ScoringCategory,
  responses: Record<string, number>,
): number {
  return category.questions.reduce((sum, q) => sum + (responses[q.id] ?? 0), 0);
}

export function computeGlobalScore(responses: Record<string, number>): number {
  return SCORING_CATEGORIES.reduce(
    (sum, cat) => sum + computeCategoryScore(cat, responses),
    0,
  );
}
